import { initialsAvatar } from '../utils/avatar';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const STATUS_COLORS = {
  pending:  { bg: 'rgba(251,191,36,0.1)',  border: 'rgba(251,191,36,0.3)',  text: '#fbbf24', label: '⏳ Pending' },
  approved: { bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.3)',   text: '#4ade80', label: '✅ Active' },
  rejected: { bg: 'rgba(248,68,68,0.1)',   border: 'rgba(248,68,68,0.3)',   text: '#f87171', label: '❌ Rejected' },
};

const AcaryaRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending'); // 'pending' | 'all'
  const [expanded, setExpanded] = useState(null); // request _id
  const [toast, setToast] = useState(null);

  // Reject / Action States
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState(null); // { id, name }
  const [rejectNotes, setRejectNotes] = useState('');

  // Approval Customisation State
  const [approvalData, setApprovalData] = useState({
    title: '',
    location: 'Varanasi, India',
    commissionTier: 30,
    chatRate: 15,
    audioRate: 15,
    videoRate: 25,
    interviewNotes: ''
  });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/acaryas/requests');
      if (res.data.success) {
        setRequests(res.data.data);
      }
    } catch (err) {
      showToast('error', 'Failed to load onboarding requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const handleExpand = (req) => {
    if (expanded === req._id) {
      setExpanded(null);
    } else {
      setExpanded(req._id);
      // Prefill approval inputs
      setApprovalData({
        title: req.coreCompetencies?.[0] || 'Acarya',
        location: 'Varanasi, India',
        commissionTier: 30,
        chatRate: 15,
        audioRate: 15,
        videoRate: 25,
        interviewNotes: ''
      });
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      const payload = {
        title: approvalData.title,
        location: approvalData.location,
        commissionTier: Number(approvalData.commissionTier),
        pricing: {
          chat: Number(approvalData.chatRate),
          audio: Number(approvalData.audioRate),
          video: Number(approvalData.videoRate)
        },
        interviewNotes: approvalData.interviewNotes || 'Cleared 10-minute panel verification interview.'
      };

      const res = await api.put(`/acaryas/requests/${id}/approve`, payload);
      if (res.data.success) {
        showToast('success', `✅ Acarya request approved! Account provisioned.`);
        setExpanded(null);
        fetchRequests();
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to approve request.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal) return;
    setActionLoading(true);
    try {
      const res = await api.put(`/acaryas/requests/${rejectModal.id}/reject`, {
        interviewNotes: rejectNotes
      });
      if (res.data.success) {
        showToast('success', 'Application rejected.');
        setRejectModal(null);
        setRejectNotes('');
        setExpanded(null);
        fetchRequests();
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to reject application.');
    } finally {
      setActionLoading(false);
    }
  };

  const displayed = requests.filter(r => tab === 'pending' ? r.status === 'pending' : true);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const Field = ({ label, value }) => (
    <div className="flex gap-4 items-start mb-2 py-1 border-b border-white/5">
      <span className="text-[10px] text-stone-500 font-bold uppercase tracking-wider w-36 shrink-0">{label}</span>
      <span className="text-xs text-stone-300 font-medium break-all">{value || <em className="text-stone-600">—</em>}</span>
    </div>
  );

  return (
    <div className="p-8 max-w-6xl mx-auto bg-[#0a0a0a] min-h-screen text-white font-serif">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-black text-[#E8B84B] font-serif">Acarya Requests</h1>
        <p className="text-stone-400 text-xs mt-1">Review applicant credentials, interview notes, set payout tiers and activate consultant profiles.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-white/5 pb-3 mb-6">
        <button
          onClick={() => { setTab('pending'); setExpanded(null); }}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all ${tab === 'pending' ? 'bg-[#E8B84B]/20 text-[#E8B84B] border border-[#E8B84B]/30' : 'text-stone-500 hover:text-stone-300'}`}
        >
          Pending Reviews ({pendingCount})
        </button>
        <button
          onClick={() => { setTab('all'); setExpanded(null); }}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all ${tab === 'all' ? 'bg-[#E8B84B]/20 text-[#E8B84B] border border-[#E8B84B]/30' : 'text-stone-500 hover:text-stone-300'}`}
        >
          All Applications ({requests.length})
        </button>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`p-4 rounded-xl border mb-6 text-xs font-bold ${toast.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
          {toast.text}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-12 h-12 border-4 border-[#E8B84B]/20 border-t-[#E8B84B] rounded-full animate-spin"></div>
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-20 bg-white/2 border border-dashed border-white/10 rounded-2xl">
          <span className="material-symbols-outlined text-stone-600 text-5xl mb-3">psychology</span>
          <p className="text-stone-500 font-bold uppercase tracking-wider text-xs">No onboarding applications found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayed.map(req => {
            const sc = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
            const isExpanded = expanded === req._id;

            return (
              <div key={req._id} className="bg-[#141210] border border-white/5 rounded-2xl overflow-hidden shadow-2xl transition-all hover:border-[#E8B84B]/20">
                
                {/* Accordion Summary */}
                <div
                  onClick={() => handleExpand(req)}
                  className="p-5 cursor-pointer flex items-center justify-between hover:bg-white/2 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <img src={req.image || initialsAvatar(req.name)} alt={req.name} className="w-12 h-12 rounded-xl object-cover border border-white/10" />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm text-stone-200">{req.name}</h3>
                        <span className="text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider border" style={{ backgroundColor: sc.bg, borderColor: sc.border, color: sc.text }}>
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-[10px] text-stone-500 mt-1">Exp: {req.experienceEducation?.yearsExperience} Yrs • Qualifications: {req.experienceEducation?.qualifications}</p>
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-stone-500">{isExpanded ? 'expand_less' : 'expand_more'}</span>
                </div>

                {/* Accordion Details */}
                {isExpanded && (
                  <div className="p-6 border-t border-white/5 bg-[#0f0e0d]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                      
                      {/* Left: Applicant Details */}
                      <div>
                        <h4 className="text-[10px] font-black uppercase text-[#E8B84B] tracking-widest mb-3">Onboarding Profile</h4>
                        <Field label="Legal Full Name" value={req.name} />
                        <Field label="Display Name" value={req.displayName} />
                        <Field label="Login Email" value={req.email} />
                        <Field label="Bio" value={req.bio} />
                        <Field label="Languages" value={req.languagesSpoken?.join(', ')} />
                        <Field label="Competencies" value={req.coreCompetencies?.join(', ')} />
                      </div>

                      {/* Right: Docs, Social & Payout */}
                      <div>
                        <h4 className="text-[10px] font-black uppercase text-[#E8B84B] tracking-widest mb-3">Verification Details</h4>
                        <Field label="Education Certificate" value={
                          req.experienceEducation?.certificationsUrl ? (
                            <a href={req.experienceEducation.certificationsUrl} target="_blank" rel="noreferrer" className="text-[#E8B84B] underline hover:text-white transition-colors">
                              View Qualification Document ↗
                            </a>
                          ) : 'Not uploaded'
                        } />
                        <Field label="PAN Card" value={req.payoutInfo?.pan} />
                        <Field label="Bank Account" value={req.payoutInfo?.bankAccount} />
                        <Field label="IFSC Code" value={req.payoutInfo?.ifsc} />
                        <Field label="YouTube Link" value={
                          req.socialProof?.youtube ? <a href={req.socialProof.youtube} target="_blank" rel="noreferrer" className="text-blue-400 underline">{req.socialProof.youtube}</a> : '—'
                        } />
                        <Field label="Website Link" value={
                          req.socialProof?.website ? <a href={req.socialProof.website} target="_blank" rel="noreferrer" className="text-blue-400 underline">{req.socialProof.website}</a> : '—'
                        } />
                        {req.sampleReadingUrl && (
                          <div className="mt-3 p-3 bg-[#E8B84B]/5 border border-[#E8B84B]/10 rounded-xl">
                            <span className="text-[9px] text-[#E8B84B] font-black uppercase tracking-wider block mb-1">🎤 Sample Reading File / Audition</span>
                            <a href={req.sampleReadingUrl} target="_blank" rel="noreferrer" className="text-xs underline font-medium text-stone-300 hover:text-white">
                              Open Sample Recording ↗
                            </a>
                          </div>
                        )}
                      </div>

                    </div>

                    {/* Admin Action Control Box */}
                    {req.status === 'pending' ? (
                      <div className="p-5 bg-white/2 border border-white/5 rounded-2xl mt-4">
                        <h4 className="text-xs font-black uppercase text-stone-200 tracking-wider mb-4">Verification Panel Action</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <label className="block text-[10px] text-stone-400 font-bold uppercase mb-2">Approved Display Title</label>
                            <input
                              type="text"
                              value={approvalData.title}
                              onChange={e => setApprovalData({ ...approvalData, title: e.target.value })}
                              className="w-full bg-[#1c1917] border border-white/10 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none"
                              placeholder="e.g. Senior Palmist"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-stone-400 font-bold uppercase mb-2">Approved Location</label>
                            <input
                              type="text"
                              value={approvalData.location}
                              onChange={e => setApprovalData({ ...approvalData, location: e.target.value })}
                              className="w-full bg-[#1c1917] border border-white/10 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-stone-400 font-bold uppercase mb-2">Commission platform fee (%)</label>
                            <input
                              type="number"
                              value={approvalData.commissionTier}
                              onChange={e => setApprovalData({ ...approvalData, commissionTier: e.target.value })}
                              className="w-full bg-[#1c1917] border border-white/10 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <label className="block text-[10px] text-stone-400 font-bold uppercase mb-2">Base Chat Rate (₹/min)</label>
                            <input
                              type="number"
                              value={approvalData.chatRate}
                              onChange={e => setApprovalData({ ...approvalData, chatRate: e.target.value })}
                              className="w-full bg-[#1c1917] border border-white/10 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-stone-400 font-bold uppercase mb-2">Base Audio Rate (₹/min)</label>
                            <input
                              type="number"
                              value={approvalData.audioRate}
                              onChange={e => setApprovalData({ ...approvalData, audioRate: e.target.value })}
                              className="w-full bg-[#1c1917] border border-white/10 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-stone-400 font-bold uppercase mb-2">Base Video Rate (₹/min)</label>
                            <input
                              type="number"
                              value={approvalData.videoRate}
                              onChange={e => setApprovalData({ ...approvalData, videoRate: e.target.value })}
                              className="w-full bg-[#1c1917] border border-white/10 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none"
                            />
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="block text-[10px] text-stone-400 font-bold uppercase mb-2">10-Minute Video Interview notes / Verification Remarks</label>
                          <textarea
                            rows="2"
                            value={approvalData.interviewNotes}
                            onChange={e => setApprovalData({ ...approvalData, interviewNotes: e.target.value })}
                            placeholder="e.g. Excellent communication skills, lineage verified, certifications cross-checked. Approved for active status."
                            className="w-full bg-[#1c1917] border border-white/10 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none resize-vertical"
                          />
                        </div>

                        <div className="flex gap-4 pt-2">
                          <button
                            onClick={() => handleApprove(req._id)}
                            disabled={actionLoading}
                            className="flex-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-green-600 to-green-700 text-white font-bold text-xs uppercase tracking-wider transition-all hover:brightness-110 active:scale-95 disabled:opacity-50"
                          >
                            {actionLoading ? 'Approving...' : '✅ Approve & Push Live'}
                          </button>
                          <button
                            onClick={() => { setRejectModal({ id: req._id, name: req.name }); setRejectNotes(''); }}
                            disabled={actionLoading}
                            className="flex-1 px-6 py-2.5 rounded-lg bg-red-600/10 border border-red-600/30 text-red-500 font-bold text-xs uppercase tracking-wider transition-all hover:bg-red-600 hover:text-white"
                          >
                            ❌ Reject Application
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-white/2 border border-white/5 rounded-xl">
                        <span className="text-[10px] text-stone-500 font-bold uppercase tracking-wider block mb-1">Interview notes / Audit Remarks</span>
                        <p className="text-xs text-stone-300 italic">"{req.interviewNotes || 'No notes available.'}"</p>
                        {req.status === 'approved' && (
                          <div className="mt-2 text-[10px] text-green-400 font-bold uppercase tracking-widest">
                            Commission Share: {100 - (req.commissionTier || 30)}% Acarya / {req.commissionTier || 30}% Platform Fee
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}

      {/* Reject Modal dialog */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#141210] border border-red-500/20 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-red-400 font-black text-sm uppercase tracking-wider mb-2">Reject Application</h3>
            <p className="text-xs text-stone-400 mb-4">Rejecting onboarding application for <strong className="text-stone-200">{rejectModal.name}</strong>. Enter interview panel notes or reason below:</p>
            
            <textarea
              rows="3"
              value={rejectNotes}
              onChange={e => setRejectNotes(e.target.value)}
              placeholder="e.g. Failed credentials validation. Certifications could not be verified by institutional registry."
              className="w-full bg-[#1c1917] border border-white/10 rounded-lg p-3 text-xs text-stone-200 outline-none resize-none mb-4"
            />

            <div className="flex gap-4">
              <button
                onClick={() => setRejectModal(null)}
                className="flex-1 py-2 rounded-lg bg-stone-800 text-stone-400 text-xs font-bold hover:bg-stone-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={actionLoading}
                className="flex-1 py-2 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Rejecting...' : '❌ Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AcaryaRequests;
